import {
  USER_LOAN_ESTIMATION_TYPE,
  USER_LOAN_PROGRAM,
  USER_OCCUPANCY,
} from '@/config/constants';
import type { DocumentReadinessSummary } from '@/apis/extended/documentRequirementsApi';
import {
  DOCUMENT_REQUIREMENT_CATALOG,
  documentTsLabelToKey,
  keysToDisplayLabels,
  labelsToKeys,
  type DocumentRequirementKey,
} from '@/utils/documentCatalog';
import type {
  LoanApplication,
  LoanApplicationDocument,
  LoanChecklistItem,
} from '@/types/LoanApplicationType';
import { User } from '@/types/UserType';

/** Legacy string matrix — source of truth for labels; interest-rate overrides merge as strings. */
const requiredDocuments = {
  [USER_LOAN_ESTIMATION_TYPE.INVESTOR]: {
    [USER_LOAN_PROGRAM.CONVENTIONAL]: {
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit Report Authorization',
        'Past two years Forms W- from all employers',
        'Paystubs for past full month',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
        'Most Recent 2 Years Tax Return Transcripts',
      ],
    },
    [USER_LOAN_PROGRAM.CASH]: {
      [USER_OCCUPANCY.PRIMARY_RESIDENCE]: [
        'Asset statements showing sufficient assets to close',
      ],
      [USER_OCCUPANCY.SECONDARY_RESIDENCE]: [
        'Asset statements showing sufficient assets to close',
      ],
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Asset statements showing sufficient assets to close',
      ],
    },
    [USER_LOAN_PROGRAM.DSCR]: {
      [USER_OCCUPANCY.SECONDARY_RESIDENCE]: [
        'Credit report authorization',
        'Personal financial statement',
        'Background report authorization',
        'Asset statements showing sufficient cash-to-close',
        'Real estate schedule of rental experience',
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit report authorization',
        'Personal financial statement',
        'Background report authorization',
        'Asset statements showing sufficient cash-to-close',
        'Real estate schedule of rental experience',
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
    },
    [USER_LOAN_PROGRAM.BRIDGE]: {
      [USER_OCCUPANCY.SECONDARY_RESIDENCE]: [
        'Credit report authorization',
        'Personal financial statement',
        'Background report authorization',
        'Asset statements showing sufficient cash-to-close',
        'Real estate schedule of rental experience',
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit report authorization',
        'Personal financial statement',
        'Background report authorization',
        'Asset statements showing sufficient cash-to-close',
        'Real estate schedule of rental experience',
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
    },
    [USER_LOAN_PROGRAM.FIX_AND_FLIP]: {
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit Report Authorization',
        'Background Report Authorization',
        'Construction Budget',
        'Real Estate Schedule of Fix and Flip Experience',
        'Asset Statements showing sufficient Assets to Close',
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
    },
    [USER_LOAN_PROGRAM.COMMERCIAL]: {
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit Report Authorization',
        'Personal Financial Statement',
        'Asset Statements showing sufficient Assets to Close',
        'Real Estate Schedule of Commercial Experience',
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
    },
  },
  [USER_LOAN_ESTIMATION_TYPE.HOME_BUYER]: {
    [USER_LOAN_PROGRAM.CONVENTIONAL]: {
      [USER_OCCUPANCY.PRIMARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'Paystubs for past full month',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
        'Most Recent 2 Years Tax Return Transcripts',
      ],
      [USER_OCCUPANCY.SECONDARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'Paystubs for past full month',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        'Real estate owned schedule',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
        'Most Recent 2 Years Tax Return Transcripts',
      ],
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'Paystubs for past full month',
        'Real estate owned schedule',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
        'Most Recent 2 Years Tax Return Transcripts',
      ],
    },
    [USER_LOAN_PROGRAM.FHA]: {
      [USER_OCCUPANCY.PRIMARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        'Paystubs for past full month',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
      ],
      [USER_OCCUPANCY.SECONDARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        'Paystubs for past full month',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
      ],
    },
    [USER_LOAN_PROGRAM.VA]: {
      [USER_OCCUPANCY.PRIMARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        'Paystubs for past full month',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
      ],
      [USER_OCCUPANCY.SECONDARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        'Paystubs for past full month',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
      ],
    },
    [USER_LOAN_PROGRAM.USDA]: {
      [USER_OCCUPANCY.PRIMARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        'Paystubs for past full month',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
      ],
    },
  },
  [USER_LOAN_ESTIMATION_TYPE.REFINANCE]: {
    [USER_LOAN_PROGRAM.CONVENTIONAL]: {
      [USER_OCCUPANCY.PRIMARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'Paystubs for past full month',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
        'Current Mortgage Statement',
        'Homeowners Insurance Declaration Page',
        'Most Recent 2 Years Tax Return Transcripts',
      ],
      [USER_OCCUPANCY.SECONDARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'Paystubs for past full month',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
        'Current Mortgage Statement',
        'Homeowners Insurance Declaration Page',
        'Most Recent 2 Years Tax Return Transcripts',
      ],
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'Paystubs for past full month',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
        'Current Mortgage Statement',
        'Homeowners Insurance Declaration Page',
        'Most Recent 2 Years Tax Return Transcripts',
      ],
    },
    [USER_LOAN_PROGRAM.FHA]: {
      [USER_OCCUPANCY.PRIMARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        'Paystubs for past full month',
        'Current Mortgage Statement',
        'Homeowners Insurance Declaration Page',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
      ],
    },
    [USER_LOAN_PROGRAM.VA]: {
      [USER_OCCUPANCY.PRIMARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        'Paystubs for past full month',
        'Current Mortgage Statement',
        'Homeowners Insurance Declaration Page',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
      ],
    },
    [USER_LOAN_PROGRAM.USDA]: {
      [USER_OCCUPANCY.PRIMARY_RESIDENCE]: [
        'Credit Report Authorization',
        'Past two years Forms W-2 from all employers',
        'End of Year Paystubs if you earn Commission, Overtime or Bonus',
        'Paystubs for past full month',
        'Current Mortgage Statement',
        'Homeowners Insurance Declaration Page',
        '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
      ],
    },
    [USER_LOAN_PROGRAM.DSCR]: {
      [USER_OCCUPANCY.SECONDARY_RESIDENCE]: [
        'Credit report authorization',
        'Personal financial statement',
        'Background report authorization',
        'Current Mortgage Statement',
        'Homeowners Insurance Declaration Page',
        'Asset statements showing sufficient cash-to-close',
      ],
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit report authorization',
        'Personal financial statement',
        'Background report authorization',
        'Current Mortgage Statement',
        'Homeowners Insurance Declaration Page',
        'Asset statements showing sufficient cash-to-close',
        'Real Estate Ownership schedule showing Rental experience',
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
    },
    [USER_LOAN_PROGRAM.BRIDGE]: {
      [USER_OCCUPANCY.SECONDARY_RESIDENCE]: [
        'Credit report authorization',
        'Personal financial statement',
        'Background report authorization',
        'Current Mortgage Statement',
        'Homeowners Insurance Declaration Page',
        'Asset statements showing sufficient cash-to-close',
      ],
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit report authorization',
        'Personal financial statement',
        'Background report authorization',
        'Current Mortgage Statement',
        'Homeowners Insurance Declaration Page',
        'Asset statements showing sufficient cash-to-close',
        'Real Estate Ownership schedule showing Rental experience',
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
    },
    [USER_LOAN_PROGRAM.COMMERCIAL]: {
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit Report Authorization',
        'Personal Financial Statement',
        'Current Mortgage Statement',
        'Insurance Declaration Page',
        'Asset Statements showing sufficient Assets to Close',
        'Real Estate Ownership Schedule showing Commercial Experience',
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
    },
  },
  [USER_LOAN_ESTIMATION_TYPE.COMMERCIAL]: {
    [USER_LOAN_PROGRAM.CASH]: {
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
    },
    [USER_LOAN_PROGRAM.COMMERCIAL]: {
      [USER_OCCUPANCY.INVESTMENT_PROPERTY]: [
        'Credit Report Authorization',
        'Personal Financial Statement',
        'Asset Statements showing sufficient Assets to Close',
        'Real Estate Ownership Schedule showing Commercial Experience',
        'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
        'Executed Entity Operating Agreement',
      ],
    },
  },
};

function mapNestedStringArraysToKeys<T extends Record<string, unknown>>(
  obj: T
): unknown {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
      out[k] = labelsToKeys(v as string[]);
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = mapNestedStringArraysToKeys(v as Record<string, unknown>);
    }
  }
  return out;
}

/** Matrix of `DocumentRequirementKey[]` — derived from `requiredDocuments`. */
export const requiredDocumentKeys = mapNestedStringArraysToKeys(
  requiredDocuments
) as Record<
  USER_LOAN_ESTIMATION_TYPE,
  Partial<
    Record<
      USER_LOAN_PROGRAM,
      Partial<Record<USER_OCCUPANCY, DocumentRequirementKey[]>>
    >
  >
>;

export type DocumentationRequestsByType = Partial<
  Record<USER_LOAN_ESTIMATION_TYPE, Partial<Record<USER_OCCUPANCY, string[]>>>
>;

export type DocumentationRequestKeysByType = Partial<
  Record<
    USER_LOAN_ESTIMATION_TYPE,
    Partial<Record<USER_OCCUPANCY, DocumentRequirementKey[]>>
  >
>;

function mapNestedKeyArraysToLabels(obj: unknown): unknown {
  if (Array.isArray(obj) && obj.every((x) => typeof x === 'string')) {
    return keysToDisplayLabels(obj as DocumentRequirementKey[]);
  }
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      out[k] = mapNestedKeyArraysToLabels(v);
    }
    return out;
  }
  return obj;
}

export const getProgramDocumentationDefaultsKeys = (
  loanProgram: USER_LOAN_PROGRAM
): DocumentationRequestKeysByType => {
  const result: DocumentationRequestKeysByType = {};
  Object.values(USER_LOAN_ESTIMATION_TYPE).forEach((estimationType) => {
    const byProgram = requiredDocumentKeys[estimationType]?.[loanProgram];
    if (!byProgram || typeof byProgram !== 'object') return;

    const occupancyMap: Partial<
      Record<USER_OCCUPANCY, DocumentRequirementKey[]>
    > = {};
    Object.entries(byProgram).forEach(([occupancy, docs]) => {
      if (!Array.isArray(docs)) return;
      occupancyMap[occupancy as USER_OCCUPANCY] = [...docs];
    });

    if (Object.keys(occupancyMap).length > 0) {
      result[estimationType] = occupancyMap;
    }
  });
  return result;
};

export const getProgramDocumentationDefaults = (
  loanProgram: USER_LOAN_PROGRAM
): DocumentationRequestsByType => {
  const keyDefaults = getProgramDocumentationDefaultsKeys(loanProgram);
  return mapNestedKeyArraysToLabels(keyDefaults) as DocumentationRequestsByType;
};

export const mergeProgramDocumentationRequests = (
  loanProgram: USER_LOAN_PROGRAM,
  overrides?: DocumentationRequestsByType
): DocumentationRequestsByType => {
  const defaults = getProgramDocumentationDefaults(loanProgram);
  if (!overrides || typeof overrides !== 'object') {
    return defaults;
  }

  const merged: DocumentationRequestsByType = { ...defaults };
  Object.entries(overrides).forEach(([estimationType, occupancyMap]) => {
    if (!occupancyMap || typeof occupancyMap !== 'object') return;
    const current = {
      ...(merged[estimationType as USER_LOAN_ESTIMATION_TYPE] || {}),
    };
    Object.entries(occupancyMap as Record<string, string[]>).forEach(
      ([occupancy, docs]) => {
        if (!Array.isArray(docs)) return;
        current[occupancy as USER_OCCUPANCY] = [...docs];
      }
    );
    merged[estimationType as USER_LOAN_ESTIMATION_TYPE] = current;
  });
  return merged;
};

export const mergeProgramDocumentationRequestKeys = (
  loanProgram: USER_LOAN_PROGRAM,
  overrides?: DocumentationRequestsByType
): DocumentationRequestKeysByType => {
  const baseStrings = mergeProgramDocumentationRequests(loanProgram, overrides);
  const out: DocumentationRequestKeysByType = {};
  Object.entries(baseStrings).forEach(([et, occMap]) => {
    if (!occMap) return;
    const inner: Partial<Record<USER_OCCUPANCY, DocumentRequirementKey[]>> = {};
    Object.entries(occMap).forEach(([occ, labels]) => {
      if (!Array.isArray(labels)) return;
      inner[occ as USER_OCCUPANCY] = labelsToKeys(labels);
    });
    out[et as USER_LOAN_ESTIMATION_TYPE] = inner;
  });
  return out;
};

export const getRequiredDocumentKeys = (
  user: User
): DocumentRequirementKey[] => {
  const keys: DocumentRequirementKey[] = [];
  if (user.loanEstimationType && user.loanProgram && user.occupancy) {
    const row =
      requiredDocumentKeys[user.loanEstimationType]?.[user.loanProgram]?.[
        user.occupancy
      ] || [];
    keys.push(...row);
  }
  return ['id_government', ...keys];
};

export const getRequiredDocuments = (user: User): string[] => {
  return keysToDisplayLabels(getRequiredDocumentKeys(user));
};

/** Short helper text aligned with Profile → Documents cards. */
const DOCUMENT_DISPLAY_DESCRIPTIONS: Record<string, string> = {
  "Driver's license or government issued ID":
    'Valid government-issued photo identification',
  'Credit Report Authorization': 'Authorization to pull your credit report',
  'Credit report authorization': 'Authorization to pull your credit report',
  'Past two years Forms W-2 from all employers':
    'W-2 forms from all employers for the past two years',
  'Past two years Forms W- from all employers':
    'W-2 forms from all employers for the past two years',
  'Paystubs for past full month': 'Most recent paystubs showing current income',
  '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)':
    'Bank statements for checking, savings, IRA, 401(k), etc.',
  'Most Recent 2 Years Tax Return Transcripts':
    'Tax return transcripts from the IRS',
  'End of Year Paystubs if you earn Commission, Overtime or Bonus':
    'End of year paystubs if you earn commission, overtime, or bonus',
  'Real estate owned schedule':
    'Schedule of all real estate properties you own',
  'Current Mortgage Statement':
    'Current mortgage statement for refinance applications',
  'Homeowners Insurance Declaration Page':
    'Current homeowners insurance policy declaration page',
  'Personal financial statement':
    'Personal financial statement showing assets and liabilities',
  'Personal Financial Statement':
    'Personal financial statement showing assets and liabilities',
  'Background report authorization': 'Authorization for background check',
  'Background Report Authorization': 'Authorization for background check',
  'Asset statements showing sufficient assets to close':
    'Bank statements showing sufficient funds for closing',
  'Asset statements showing sufficient cash-to-close':
    'Bank statements showing sufficient funds for closing',
  'Real estate schedule of rental experience':
    'Schedule showing your rental property experience',
  'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)':
    'Business entity documentation',
  'Executed Entity Operating Agreement':
    'Signed operating agreement for your business entity',
  'Construction Budget':
    'Detailed construction budget for fix and flip projects',
  'Real Estate Schedule of Fix and Flip Experience':
    'Schedule showing your fix and flip experience',
  'Real Estate Schedule of Commercial Experience':
    'Schedule showing your commercial real estate experience',
  'Real Estate Ownership schedule showing Rental experience':
    'Schedule showing your rental property ownership experience',
  'Asset Statements showing sufficient Assets to Close':
    'Bank statements showing sufficient funds for closing',
  'Real Estate Ownership Schedule showing Commercial Experience':
    'Schedule showing your commercial real estate ownership experience',
  'Insurance Declaration Page':
    'Current homeowners insurance policy declaration page',
};

export function documentDescriptionForDisplayLabel(
  displayLabel: string
): string {
  return (
    DOCUMENT_DISPLAY_DESCRIPTIONS[displayLabel] ||
    'Required document for loan application'
  );
}

/**
 * Loan application checklist rows aligned with Profile → Documents
 * (`getRequiredDocuments` / catalog), preserving completion state from the server
 * when keys or labels match.
 */
function profileSubmissionForKey(
  docReadiness: DocumentReadinessSummary | undefined | null,
  reqKey: DocumentRequirementKey
) {
  return docReadiness?.submissions?.find(
    (s) => s.requirementKey === reqKey && Boolean(s.storage?.url)
  );
}

const REFINANCE_DOCUMENT_KEYS: DocumentRequirementKey[] = [
  'mortgage_statement_current',
  'insurance_declaration',
  'payoff_statement',
];

export function mergeLoanChecklistWithProfileDocuments(
  user: User | undefined | null,
  application: LoanApplication,
  docReadiness?: DocumentReadinessSummary | null
): LoanChecklistItem[] {
  const serverList = application.checklist ?? [];
  if (!user?.loanEstimationType || !user?.loanProgram || !user?.occupancy) {
    return serverList;
  }
  const keys = getRequiredDocumentKeys(user);
  const labels = keysToDisplayLabels(keys);
  const labelKey = documentTsLabelToKey as Record<
    string,
    DocumentRequirementKey
  >;

  const findPrev = (reqKey: DocumentRequirementKey, label: string) => {
    const byStableKey = serverList.find((c) => c.key === reqKey);
    if (byStableKey) return byStableKey;
    return serverList.find((c) => {
      const mapped = labelKey[c.label];
      return mapped === reqKey || c.label === label;
    });
  };

  // The profile's list is purchase-shaped. A refinance also needs the loan
  // being paid off and the policy on the home it secures.
  const optionalKeys = new Set<DocumentRequirementKey>();
  if (application.flowProfile === 'refinance') {
    for (const k of REFINANCE_DOCUMENT_KEYS) {
      if (!keys.includes(k)) {
        keys.push(k);
        labels.push(keysToDisplayLabels([k])[0]);
      }
    }
    optionalKeys.add('payoff_statement');
  }

  return keys.map((reqKey, i) => {
    const label = labels[i];
    const prev = findPrev(reqKey, label);
    const profileSubmission = profileSubmissionForKey(docReadiness, reqKey);
    const completed = prev?.completed ?? Boolean(profileSubmission);
    return {
      _id: prev?._id,
      key: reqKey,
      label,
      required: !optionalKeys.has(reqKey),
      ...(optionalKeys.has(reqKey) ? { requiredForSubmit: false } : {}),
      completed,
      completedAt:
        prev?.completedAt ??
        (profileSubmission?.createdAt
          ? String(profileSubmission.createdAt)
          : undefined),
      documentIds: prev?.documentIds,
    };
  });
}

export type UploadedDocumentRow = {
  id: string;
  fileName: string;
  relatedLabel?: string;
  url?: string;
  uploadedAt?: string;
  kind: 'profile' | 'loan_application' | 'legacy';
  loanDocumentId?: string;
};

function fileNameFromUrl(url: string): string {
  const segment = url.split('/').pop()?.split('?')[0] || 'Document';
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** All user uploads in one list (profile submissions, mortgage file, legacy bucket). */
export function collectUploadedDocumentsForDisplay(params: {
  user?: User | null;
  docReadiness?: DocumentReadinessSummary | null;
  loanDocs: LoanApplicationDocument[];
  checklistRows?: LoanChecklistItem[];
}): UploadedDocumentRow[] {
  const { user, docReadiness, loanDocs, checklistRows = [] } = params;
  const seenUrls = new Set<string>();
  const seenLoanIds = new Set<string>();
  const rows: UploadedDocumentRow[] = [];

  const labelByChecklistRef = new Map<string, string>();
  for (const r of checklistRows) {
    if (r._id) labelByChecklistRef.set(r._id, r.label);
    labelByChecklistRef.set(r.key, r.label);
  }

  for (const sub of docReadiness?.submissions ?? []) {
    const url = sub.storage?.url?.trim();
    if (!url || seenUrls.has(url)) continue;
    seenUrls.add(url);
    const catalog =
      DOCUMENT_REQUIREMENT_CATALOG[
        sub.requirementKey as DocumentRequirementKey
      ];
    rows.push({
      id: `sub-${sub._id}`,
      fileName: sub.fileName || catalog?.displayLabel || 'Document',
      relatedLabel: catalog?.displayLabel,
      url,
      uploadedAt: sub.createdAt,
      kind: 'profile',
    });
  }

  for (const d of loanDocs) {
    if (seenLoanIds.has(d._id)) continue;
    seenLoanIds.add(d._id);
    const ref = d.checklistItemId ?? '';
    rows.push({
      id: d._id,
      fileName: d.title || d.file?.originalName || 'Document',
      relatedLabel: ref
        ? labelByChecklistRef.get(ref)
        : d.category || undefined,
      uploadedAt: d.uploadedAt ?? d.createdAt,
      kind: 'loan_application',
      loanDocumentId: d._id,
    });
  }

  for (const raw of user?.documents ?? []) {
    const url = typeof raw === 'string' ? raw.trim() : '';
    if (!url || seenUrls.has(url)) continue;
    seenUrls.add(url);
    rows.push({
      id: `legacy-${url}`,
      fileName: fileNameFromUrl(url),
      url,
      kind: 'legacy',
    });
  }

  rows.sort((a, b) => {
    const ta = a.uploadedAt ? Date.parse(a.uploadedAt) : 0;
    const tb = b.uploadedAt ? Date.parse(b.uploadedAt) : 0;
    return tb - ta;
  });

  return rows;
}

export function mortgageChecklistProgress(rows: LoanChecklistItem[]): {
  done: number;
  total: number;
  pct: number;
} {
  const required = rows.filter((c) => c.required);
  const total = required.length;
  const done = required.filter((c) => c.completed).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return { done, total, pct };
}

/** Maps loan program to `latestRates.programs` object keys (admin interest-rate config). */
export const LOAN_PROGRAM_TO_RATE_CONFIG_KEY: Record<
  USER_LOAN_PROGRAM,
  string
> = {
  [USER_LOAN_PROGRAM.CASH]: 'cash',
  [USER_LOAN_PROGRAM.DSCR]: 'dscr',
  [USER_LOAN_PROGRAM.CONVENTIONAL]: 'conventional',
  [USER_LOAN_PROGRAM.FHA]: 'fha',
  [USER_LOAN_PROGRAM.USDA]: 'usda',
  [USER_LOAN_PROGRAM.VA]: 'va',
  [USER_LOAN_PROGRAM.COMMERCIAL]: 'commercial',
  [USER_LOAN_PROGRAM.FIX_AND_FLIP]: 'fixAndFlip',
  [USER_LOAN_PROGRAM.BRIDGE]: 'bridge',
  [USER_LOAN_PROGRAM.HELOC]: 'heloc',
  [USER_LOAN_PROGRAM.HELOAN]: 'heloan',
  [USER_LOAN_PROGRAM.SECOND_MORTGAGE]: 'secondMortgage',
  [USER_LOAN_PROGRAM.DEFAULT]: 'conventional',
};

export function documentationRequestsForProgramFromRates(
  loanProgram: USER_LOAN_PROGRAM,
  ratePrograms:
    | Record<string, { documentationRequests?: DocumentationRequestsByType }>
    | undefined
): DocumentationRequestsByType {
  const programKey =
    LOAN_PROGRAM_TO_RATE_CONFIG_KEY[loanProgram] ?? 'conventional';
  const stored = ratePrograms?.[programKey]?.documentationRequests;
  return mergeProgramDocumentationRequests(
    loanProgram,
    stored && typeof stored === 'object' ? stored : undefined
  );
}

export type { DocumentRequirementKey } from '@/utils/documentCatalog';
